/**
 * React adapter for the playground stream. The package's `usePlaygroundStream`
 * already owns the session state machine (execute → awaiting → resuming →
 * terminal) and the StrictMode cancel-not-dispose lifecycle; we just bind it to
 * the portal's cookie-auth transport.
 *
 * The session keeps only `HTTP <status>` from a start the BFF refused and drops
 * the body that says why, so the transport is wrapped to read that body first,
 * from a copy, and to note whether the BFF answered at all.
 */

import type { PlaygroundTransport, UsePlaygroundStream } from '@evt/flow-stream-ui'
import { usePlaygroundStream } from '@evt/flow-stream-ui'
import { useState } from 'react'
import { apiErrorFrom, type PortalApiError, playgroundTransport } from '../api/portal-client'

/** How the last start went, before the stream took over. */
export interface RunStart {
  /** The BFF's refusal, with the issue that says why. Null when it accepted the start or never answered. */
  refusal: PortalApiError | null
  /** Whether the BFF answered the start with a stream it accepted. */
  accepted: boolean
}

export const NO_START: RunStart = { refusal: null, accepted: false }

/**
 * `base` with an `execute` that reports how each start went. The response goes
 * on to the session untouched: the body is read from a copy. A 401 is left to
 * the transport, which sends the person to sign in; an aborted start reports
 * nothing new, since the person cancelled it.
 */
export function reportingStarts(base: PlaygroundTransport, report: (start: RunStart) => void): PlaygroundTransport {
  return {
    execute: async (input, signal) => {
      report(NO_START)
      const res = await base.execute(input, signal)
      if (res.ok) report({ refusal: null, accepted: true })
      else if (res.status !== 401) report({ refusal: await apiErrorFrom(res.clone()), accepted: false })
      return res
    },
    resume: base.resume
  }
}

export interface RunStream extends UsePlaygroundStream {
  start: RunStart
}

export function useRunStream(): RunStream {
  const [start, setStart] = useState<RunStart>(NO_START)
  // Built once: the package builds its session from the first transport it is given.
  const [transport] = useState(() => reportingStarts(playgroundTransport, setStart))
  return { ...usePlaygroundStream(transport), start }
}
